import { cloneDeep, merge, includes, isEmpty } from 'lodash';
import { getErrorMessage, getAccessToken } from '~/account/utilities/helpers';
import { errorMessageOverrides } from '~/account/utilities/helpers';
import {
    ensureValidToken,
    refreshAccessToken
} from '~/account/utilities/tokenRefresh';

const removeSlashesRegex = '/^\\/+|\\/+$/';

const buildApiUrl = (route, isLegacy) => {
    const baseUrl = process.env.GATSBY_CMS_BASE_URL.replace(
        removeSlashesRegex,
        ''
    );

    let apiBase = process.env.GATSBY_CMS_AMP_API_BASE.replace(
        removeSlashesRegex,
        ''
    );

    if (isLegacy) {
        apiBase = process.env.GATSBY_CMS_AMP_API_BASE_LEGACY_INVOICE.replace(
            removeSlashesRegex,
            ''
        );
    }

    return `${baseUrl}/${apiBase}/${route}`;
};

const requiresAuth = (route) => route.startsWith('api-user');

const executeRequest = async (url, fetchOptions) => {
    const response = await fetch(url, fetchOptions);

    try {
        const json = await response.json();
        return { json, ok: response.ok, status: response.status };
    } catch (error) {
        if (!response.ok) {
            return {
                json: {
                    error: {
                        id: response.status,
                        message: response.statusText
                    }
                },
                ok: false,
                status: response.status
            };
        } else {
            return {
                json: { success: true },
                ok: response.ok,
                status: response.status
            };
        }
    }
};

/**
 * @param {String} route
 * @param {Object} options {@link https://developer.mozilla.org/en-US/docs/Web/API/fetch#options}
 * @param {Boolean} returnJson
 * @param {Boolean} isLegacy
 * @returns {Promise<any>}
 */
export const ampFetch = async ({
    route,
    options,
    returnJson = false,
    isLegacy = false
}) => {
    const url = buildApiUrl(route, isLegacy);
    const needsAuth = requiresAuth(route);

    if (needsAuth) {
        const token = await ensureValidToken();
        if (!token) {
            return new Response(
                {
                    error: {
                        id: 'SESSION_EXPIRED',
                        message:
                            'Your session has expired. Please sign in again.'
                    }
                },
                false
            );
        }
    }

    const fetchOptions = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': needsAuth ? `Bearer ${getAccessToken()}` : null
        }
    };

    merge(fetchOptions, options);

    try {
        let result = await executeRequest(url, fetchOptions);

        if (result.status === 401 && needsAuth) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                fetchOptions.headers['Authorization'] = `Bearer ${newToken}`;
                result = await executeRequest(url, fetchOptions);
            } else {
                return new Response(
                    {
                        error: {
                            id: 'SESSION_EXPIRED',
                            message:
                                'Your session has expired. Please sign in again.'
                        }
                    },
                    false
                );
            }
        }

        return returnJson
            ? result.json
            : new Response(result.json, result.ok, result.status);
    } catch (error) {
        console.error(error);

        return new Response({
            error: {
                id: 'UNKNOWN',
                message: error.message
            }
        });
    }
};

/**
 * Normalize the response object.
 * AMP API has many response objects with inconsistent HTTP codes.
 */

const successStatuses = [
    'SUCCESS',
    'DRAFT',
    'canceled',
    'active',
    'pending',
    'OPEN'
];

export class Response {
    constructor(response, ok, httpStatus = null) {
        this.errorCode = null;
        this.errorMessage = null;
        this.success = null;
        this.refunds = null;
        this.failedItems = null;
        this.statusCode = null;
        this.code = null;
        this.httpStatus = httpStatus;
        this.data = cloneDeep(response);

        const { error, errors, status, message, refunds, success, code } =
            response;
        const statusCode = response.status_code;

        if (!isEmpty(refunds)) {
            this.refunds = refunds;
        }

        if (statusCode) {
            this.statusCode = statusCode;
        }

        if (code) {
            this.code = code;
        }

        if (errors && Array.isArray(errors.items)) {
            this.failedItems = errors.items;
        } else if (error && Array.isArray(error.message)) {
            this.failedItems = error.message;
        }

        const hasFieldErrors =
            !isEmpty(errors) &&
            !(Object.keys(errors).length === 1 && Array.isArray(errors.items));

        if (hasFieldErrors) {
            this.errorCode = 'API_ERROR';
        } else if (success === false) {
            this.errorCode = code || 'API_ERROR';
        } else {
            this.errorCode = !includes(successStatuses, status) ? status : null;
        }

        if (this.errorCode) {
            this.errorMessage = this.resolveErrorMessage({
                message,
                errors,
                error
            });
        }

        if (success === true) {
            this.success = true;
        } else if (success === false) {
            this.success = false;
        } else {
            this.success = ok && !this.errorCode;
        }

        if (this.success) {
            delete this.data.code;
            delete this.data.status;
            delete this.data.message;
        }
    }

    extractFromErrors(errors) {
        if (errors.non_field_errors) {
            return Array.isArray(errors.non_field_errors)
                ? errors.non_field_errors[0]
                : errors.non_field_errors;
        }
        if (errors.detail) {
            return errors.detail;
        }
        const keys = Object.keys(errors).filter((key) => key !== 'items');
        if (!keys.length) {
            return null;
        }
        const value = errors[keys[0]];
        return Array.isArray(value) ? value[0] : value;
    }

    resolveErrorMessage({ message, errors, error }) {
        if (!isEmpty(errors)) {
            const extracted = this.extractFromErrors(errors);
            if (extracted) {
                return extracted;
            }
        }
        if (errorMessageOverrides.hasOwnProperty(this.errorCode)) {
            return getErrorMessage(this.errorCode);
        }
        if (message) {
            return message;
        }
        if (error?.message && typeof error.message === 'string') {
            return error.message;
        }
        return getErrorMessage(this.errorCode);
    }
}
